import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ScrollView, Text, XStack, YStack } from "tamagui";
import { useUser } from "@utils/store/user/useUser";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import appToast from "@components/Toast";
import { dataUrlToBlob, fetchCustomerContexts, fetchCylinderByBarcode, getRegisterApiErrorMessage, registerWaitingSetup, registerWalkIn, refreshCustomerSession, uploadRegisterFile, WalkInCylinder } from "@screen/Onboarding/registerApi";
import { pickChooseIdentity } from "@utils/func/identity";
import type { IdentityContext } from "@utils/store/user/useUser";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useOrder } from "@utils/store/cart/useOrder";
import { buildXContextHeader } from "@utils/api/headers";
import { addProductItem, createDraftWithGas, draftFromItemRes } from "../api/kioskApi";
import { kiosk } from "../kioskTheme";
import { useKiosk } from "../useKiosk";
import IdentityCheckCard from "./IdentityCheckCard";
import CylinderUsageCard, { CylinderUsage, cylinderUsageValid } from "./CylinderUsageCard";
import PlateCard, { PlatePhoto } from "./PlateCard";
import AccessoryCard from "./AccessoryCard";
import ContractSheet from "./ContractSheet";
import WaitingSetupDone from "./WaitingSetupDone";

/**
 * 「首次加入設定」全屏一段（一次性）：身分核對 →（新客戶）鋼瓶數量+用途 → 鐵牌(選填) → 配件 → 契約 → 完成。
 * 由訂購入口（首頁「立即預約叫桶」/「瓦斯訂購」tab / kiosk 進入）觸發；完成後清 needIdentityCheck，之後結帳頁保持乾淨。
 *
 * 完成收尾（見 docs/2026-07-10-onboarding-walkin-registration-design.md §3.5）：
 * 新客戶 → 上傳鐵牌/簽名（S3 key）→ POST /register/walk-in 建立新註冊客戶；
 * 已比對到既有客戶（IdentityCheckCard 命中） → 同樣呼叫 walk-in，帶 matchedCisId 綁定既有 CIS。
 * Phase 2：walk-in body 額外帶 onboarding 過程收集到的 entryLandline（若有），
 * 後端會自己 cascade 手機→市話+地址比對，前端不必自行判斷是否綁定。
 */
const FirstJoinSetup = ({
  open,
  onDone,
  onClose,
  assumeMatched,
  skipIdentity,
  disablePlateMatch,
}: {
  open: boolean;
  onDone: () => void;
  onClose: () => void;
  /** 覆寫身分比對傾向；未給時沿用 store 的 pendingReview（自述既有客戶）。
      結帳頁「不是我 → 新客戶」再開設定時傳 false，避免又跳一次「是我/不是我」。 */
  assumeMatched?: boolean;
  /** 跳過身分核對步驟、直接以新客戶收資料（鋼瓶/鐵牌/配件/合約）。
      QR 一鍵下單的 /kiosk/join 用：onboarding 已做過比對，這裡不再問「是我/不是我」。 */
  skipIdentity?: boolean;
  /** 關閉鐵牌讀碼比對（只拍照存證，不跳「是我/不是我」）。
      與 skipIdentity 分開：首頁入口要「不問身分」但「鐵牌仍要比對」，
      QR /kiosk/join 才是兩者都關（onboarding 已比對過身分，再跳比對卡會與既有結論打架）。 */
  disablePlateMatch?: boolean;
}) => {
  const { chooseIdentity, setChooseIdentity, userData } = useUser();
  const { lineAccessToken } = useLineAuth();
  const { setCusJwtToken } = useCusAuth();
  const { setLoading } = useUtilsStore();
  const {
    pendingReview,
    setPendingReview,
    claimedExisting,
    setNeedIdentityCheck,
    setContractSigned,
    entrySource,
    entryShopCode,
    entrySupplierId,
    phoneVerifiedTicket,
    matchedCisId,
    setMatchedCisId,
    entryLandline,
    entryAddress,
    entryAddressNote,
    contactPhone,
    setContactPhone,
  } = useKiosk();
  const assume = assumeMatched ?? pendingReview;
  const [identityResolved, setIdentityResolved] = useState(!!skipIdentity);
  const [identityType, setIdentityType] = useState<"existing" | "new" | null>(skipIdentity ? "new" : null);
  /** 身分確認卡上客戶輸入的門牌號（已正規化）：綁定既有客戶時一併送後端 verifyClaimedCis 驗證。 */
  const [identityHouseNo, setIdentityHouseNo] = useState<string | undefined>(undefined);
  const [cylUsage, setCylUsage] = useState<CylinderUsage>({ has: null, qty: 1, usage: null });
  const [needAccessory, setNeedAccessory] = useState<boolean | null>(null);
  const [plates, setPlates] = useState<PlatePhoto[]>([]);
  const [contractOpen, setContractOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  // 等待安排瓦斯行客戶（無 supplier）完成設定 → 顯示「資料已備妥」收尾頁（waiting mockup B）
  const [waitingDone, setWaitingDone] = useState(false);
  // 鐵牌拍照讀到 barcode → cylinder-by-barcode 命中的既有客戶（等使用者確認是否本人）。
  // 2026-07-15 拍板：鐵牌讀碼比對「不受 cylinder_scan 開關限制」（開關只管掃鋼瓶入口）。
  const [plateBinding, setPlateBinding] = useState(false);
  // 比對卡是「純選項」（2026-07-15 拍板）：是我/不是我都只是選擇、不打 API；卡片保留可反悔。
  // 實際綁定延到最下方按鈕送出時（plateChoice==="bind" → bindByPlate；"new" → 照新客戶簽約送出）。
  const [plateChoice, setPlateChoice] = useState<null | "bind" | "new">(null);

  // 比對狀態一律「從 plates 衍生」，不另存 ref/state：
  // 條碼與命中客戶存在照片本身（PlatePhoto.barcode/cisId），刪掉照片 → 這裡自動重算，
  // 不會像先前用累加 ref 那樣「刪了照片、衝突卻解不掉、比對回不來」。
  const plateCisIds = Array.from(new Set(plates.filter((p) => p.cisId).map((p) => p.cisId as string)));
  // 多張鐵牌命中「不同客戶」= 歸屬不明 → 放棄自動綁定（2026-07-15 拍板，交 AW 核對）。
  const plateConflict = plateCisIds.length > 1;
  // 恰好命中同一位客戶 → 出「是我/不是我」選項（取第一張帶該 cisId 的照片，含遮罩電話）。
  const plateMatch =
    plateCisIds.length === 1
      ? (() => {
          const p = plates.find((x) => x.cisId === plateCisIds[0]);
          return p ? { barcode: p.barcode || "", cisId: p.cisId as string, phone: p.phone || "", addr: p.addr || "" } : null;
        })()
      : null;

  // 進入「多客戶衝突」時提示一次；衝突解除（刪掉照片後）重置旗標，之後若再衝突可再提示。
  const plateConflictToastRef = useRef(false);
  useEffect(() => {
    if (plateConflict && !plateConflictToastRef.current) {
      plateConflictToastRef.current = true;
      appToast.info("多支鋼瓶分屬不同客戶，將以新客戶完成設定，由瓦斯行核對");
    } else if (!plateConflict) {
      plateConflictToastRef.current = false;
    }
  }, [plateConflict]);

  // 命中的既有客戶換了（或刪照片後從無到有 / 有到無）→ 之前選的「是我/不是我」作廢，重新必答。
  useEffect(() => {
    setPlateChoice(null);
  }, [plateMatch?.cisId]);

  // 地址優先取 onboarding 地址步驟存進 kiosk store 的 entryAddress（持久、不被 /customer/contexts 洗掉）；
  // 沒有才退回 chooseIdentity（既有客戶帶入的情境）。新客戶 walk-in 的 address.full_address 靠這個帶。
  const address = entryAddress || chooseIdentity?.choose_address?.full_address || "";

  /** 綁定/建立成功後刷新身分（同 onboarding 的 refreshIdentityContexts）：避免殘留舊 context → X-Context 403。
      回傳刷新後選定的 context，供後續（草稿重建）使用。 */
  const refreshIdentityContexts = async (jwt: string): Promise<IdentityContext | null> => {
    try {
      const res = await fetchCustomerContexts(jwt);
      const data = res.data?.data;
      const contexts = (data?.contexts ?? []) as IdentityContext[];
      const next = pickChooseIdentity(contexts, {
        currentChooseIdentity: chooseIdentity,
        defaultContextId: data?.default_context_id,
      });
      if (next) setChooseIdentity(next);
      // 換身分清空持久的結帳聯絡電話，避免 OTP 發到上一個身分殘留的號碼（真簡訊）
      setContactPhone("");
      // eslint-disable-next-line no-console
      console.log(`[註冊] 身分已刷新 → context_id=${next?.context_id ?? "無"}（共 ${contexts.length} 個身分）；結帳聯絡電話已重置`);
      return next ?? null;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log("[註冊] 身分刷新失敗（將沿用現有身分，可能導致 X-Context 403）:", err);
      return null;
    }
  };

  /** 完成設定/綁定後身分換了 context → 舊 context 建的購物車草稿對新身分必 403（無權限操作此訂單草稿）。
      把購物車的瓦斯品項用「新身分」原樣重建成新草稿（品項不丟；優惠券由新草稿的自動帶券重跑）。
      重建不了（缺地址/瓦斯行等）→ 至少清掉舊草稿，避免後續套券/preview/送單全卡 403。 */
  const rebuildDraftForContext = async (jwt: string, ctx: IdentityContext | null) => {
    const { draftOrderId, draft, productList, deliveryTime, clearProducts, syncDraft, setDeliveryTime } = useOrder.getState();
    if (!draftOrderId) return; // 沒草稿（首頁入口）→ 不用搬
    const savedDeliveryTime = { ...deliveryTime }; // 使用者在結帳引導已選的配送時段，重建不可洗掉
    // 跨瓦斯行不搬：品項的 price_id 是「原草稿瓦斯行」的價目，綁定後若換到別家
    //（鐵牌綁到他行既有客戶），拿舊行 price_id 打新行要嘛全失敗、要嘛價格錯 → 只清不搬。
    const oldSupplierId: string | undefined = (draft as { supplier_id?: string } | null)?.supplier_id;
    const newSupplierId = ctx?.primary_supplier?.supplier_id || entrySupplierId;
    if (oldSupplierId && newSupplierId && oldSupplierId !== newSupplierId) {
      // 跨瓦斯行：品項 price_id 是舊行價目，拿去新行必失敗/價格錯 → 無法沿用重建。
      // 但「不清空」保留現有購物車，避免把使用者丟到空車（空車會觸發 confirm 空車守門 → 訂單消失/迴圈）。
      // eslint-disable-next-line no-console
      console.log(`[註冊] 草稿重建跳過（瓦斯行不同：${oldSupplierId} → ${newSupplierId}）→ 保留現有購物車，不清空`);
      return;
    }
    const gasItems = productList
      .filter((p) => p.type === "gas" || p.type === "cis_gas")
      .map((p) => ({
        type: p.type,
        price_id: p.price_id,
        gas_id: p.gas_id,
        gas_type: p.gas_type,
        kilogram: p.kilogram,
        unit_price: p.unit_price,
        qty: p.qty,
        usage_location: p.usage_location,
        note: p.note ?? null,
        floor: p.floor,
        is_elevator: p.is_elevator,
        customer_delivery_id: null, // 舊 context 的配送位 id 不可帶進新 context；BE 依內容 upsert
      }));
    const addrId = ctx?.choose_address?.customer_address_id;
    const supId = ctx?.primary_supplier?.supplier_id || entrySupplierId;
    if (!ctx || !addrId || !supId || gasItems.length === 0) {
      // 缺條件無法重建 → 不清空，保留現有草稿/品項（先前一律先清會把人丟到空車 → 訂單炸掉）。
      // eslint-disable-next-line no-console
      console.log(`[註冊] 草稿重建條件不足（context=${ctx?.context_id ?? "無"}｜地址=${addrId ?? "無"}｜瓦斯行=${supId ?? "無"}｜瓦斯品項 ${gasItems.length} 筆）→ 保留現有購物車，不清空`);
      return;
    }
    try {
      const xContext = buildXContextHeader(ctx) || "";
      const [first, ...rest] = gasItems;
      // 先用新身分把整份草稿建好（成功前不動現有購物車）
      let newDraft = await createDraftWithGas({ supplierId: supId, customerAddressId: addrId, gasItem: first, jwtToken: jwt, xContext });
      for (const it of rest) {
        const res = await addProductItem({ type: "gas", draftId: newDraft?.draft_id, body: it, jwtToken: jwt, xContext });
        newDraft = draftFromItemRes(res) || newDraft;
      }
      if (newDraft) {
        // 新草稿確定建好，才「原子替換」：清舊 → 套新 → 還原使用者已選時段。
        clearProducts();
        syncDraft(newDraft);
        setDeliveryTime(savedDeliveryTime); // clearProducts 會重置 deliveryTime；syncDraft 不會回填 → 手動還原
      }
      // eslint-disable-next-line no-console
      console.log(`[註冊] 草稿已用新身分重建 → draft_id=${newDraft?.draft_id ?? "失敗"}（瓦斯品項 ${gasItems.length} 筆）`);
    } catch (err) {
      // 重建失敗 → 不清空，保留原草稿/品項，讓使用者不會落到空車（可再重試）。
      // eslint-disable-next-line no-console
      console.log("[註冊] 草稿重建失敗，保留現有購物車:", err);
    }
  };
  // userData.phone 靠 /customer/home 回填，新註冊（未綁定）客戶可能拿不到 →
  // fallback 到註冊收尾存的 contactPhone（本次 OTP 驗證過的手機，與票證同號）
  const phone = userData?.phone || contactPhone || "";
  const isNew = identityType === "new";
  const cylinderPending = isNew && !cylinderUsageValid(cylUsage);
  // 鐵牌比對卡（是您本人嗎？）未回應時必答，不可跳過直接簽約送出（2026-07-15 拍板）；
  // 選了任一選項（plateChoice）即視為已回應，卡片保留可反悔。
  const plateMatchPending = !!plateMatch && plateChoice === null && !plateBinding;
  const canFinish = identityResolved && !cylinderPending && !plateMatchPending && !submitting && !plateBinding;
  // 優先用已解析出的真實 supplier_id（chooseIdentity 既有綁定 / onboarding shopCode 解析出）；
  // 沒有則退回帶原始 shopCode，讓後端反查（二擇一，見 walk-in body 設計）。
  const effectiveSupplierId = chooseIdentity?.primary_supplier?.supplier_id || entrySupplierId || undefined;
  const effectiveShopCode = effectiveSupplierId ? undefined : entryShopCode || undefined;

  if (!open) return null;

  const complete = async (signatureDataUrl?: string) => {
    // 無 supplier/shopCode = 等待安排瓦斯行客戶（waiting list）：設定資料走 waiting-setup 暫存，
    // 不建 CIS；平台對接時帶入（2026-07-15 更正版：等待客戶可完成設定、只有訂購鎖定）。
    const isWaitingSetup = !effectiveSupplierId && !effectiveShopCode;
    setSubmitError("");
    setSubmitting(true);
    setLoading(true);
    try {
      // 新客戶才收鋼瓶/鐵牌/簽名；既有客戶（IdentityCheckCard 命中）本次只做帳號綁定。
      let plateKeys: { key: string }[] = [];
      if (isNew && plates.length) {
        const uploaded = await Promise.all(plates.map((p) => uploadRegisterFile(p.file, "plate", lineAccessToken || "", effectiveSupplierId)));
        plateKeys = uploaded
          .map((r) => r.data?.data?.s3_key)
          .filter((key): key is string => !!key)
          .map((key) => ({ key }));
      }

      let signatureKey: { key: string } | undefined;
      if (isNew && signatureDataUrl) {
        const blob = dataUrlToBlob(signatureDataUrl);
        const uploaded = await uploadRegisterFile(blob, "signature", lineAccessToken || "", effectiveSupplierId);
        const key = uploaded.data?.data?.s3_key;
        if (key) signatureKey = { key };
      }

      const cylinder: WalkInCylinder = isNew
        ? { has: !!cylUsage.has, qty: cylUsage.has ? cylUsage.qty : 0, usage: cylUsage.usage }
        : { has: false, qty: 0, usage: null }; // 既有客戶本次未收集鋼瓶資料

      if (isWaitingSetup) {
        await registerWaitingSetup({
          accessToken: lineAccessToken || "",
          cylinder,
          plates: plateKeys,
          signature: signatureKey,
        });
        // eslint-disable-next-line no-console
        console.log("[註冊] 等待安排瓦斯行：設定資料已暫存（waiting-setup，掛等待列），對接時帶入");
        setNeedIdentityCheck(false); // 資料已備妥 → 首頁不再跳完成設定
        setPendingReview(false);
        setContractSigned(true);
        setWaitingDone(true); // 「資料已備妥！我們會盡快對接」收尾頁（B）
        return;
      }

      const res = await registerWalkIn({
        accessToken: lineAccessToken || "",
        phoneVerifiedTicket: phoneVerifiedTicket || undefined,
        supplierId: effectiveSupplierId,
        shopCode: effectiveShopCode,
        // 新客戶（走完所有比對關卡、identityType==="new"）：不帶 matchedCisId，且 isNew:true 讓後端完全不做既有客戶比對。
        // 既有客戶（IdentityCheckCard 命中且使用者按「是我」）：帶確認過的 matchedCisId，後端會驗證此筆是否比對得上。
        matchedCisId: isNew ? undefined : matchedCisId || undefined,
        isNew,
        // 自稱（身分頁按鈕的直接表達）：確認卡按「是我」＝自稱既有；
        // 其餘沿用 onboarding 流程記下的按鈕答案（不是我不覆寫；未表達視同新客戶）
        claimedExisting: !isNew || claimedExisting === true,
        entrySource: entrySource || undefined,
        customer: {
          name: userData?.name || userData?.customer_name || "",
          // 有拿到就送（讓後端走新鮮 OTP 驗證）；拿不到就省略，後端自解
          // （已註冊客戶用 main_phone / 票證內號碼），不再靠前端易失狀態湊。
          phone: phone || undefined,
          landline: entryLandline || undefined, // onboarding 收集到的市話（若有），供後端 cascade 手機→市話+地址比對
          landline_input: entryLandline || undefined, // 同值，但語意是「客戶填了什麼」：後端存 registration 供 AW 核對
          // note：onboarding 地址頁「給配送員的指示」（entryAddressNote），
          // 後端寫入兩張地址表的 extra，不會成為地址字串的一部分。
          // house_no：身分確認卡上客戶輸入的門牌號，只在綁定既有客戶時帶（新客戶沒有要驗的宣稱對象）。
          address: { full_address: address, note: entryAddressNote || undefined, house_no: isNew ? undefined : identityHouseNo },
        },
        cylinder,
        plates: plateKeys,
        signature: signatureKey,
        contractAgreed: isNew,
      });

      const data = res.data?.data;
      // eslint-disable-next-line no-console
      console.log(`[註冊] 完成加入設定 walk-in → ${isNew ? "『新客戶』建立" : `『綁定既有』（宣稱 matchedCisId=${matchedCisId || "無"}）`} → cis_id=${data?.cis_id}｜customer_id=${data?.customer_id}｜review_status=${data?.review_status}`);
      if (data?.cis_id) setMatchedCisId(data.cis_id); // 換成後端權威 cis_id
      // 綁定/建立成功 → 重新登入刷新客戶身分，首頁才抓得到資料（免整頁重載）
      const jwt = await refreshCustomerSession(lineAccessToken || "");
      if (jwt) {
        setCusJwtToken(jwt);
        const nextCtx = await refreshIdentityContexts(jwt);
        await rebuildDraftForContext(jwt, nextCtx); // 舊 context 的草稿對新身分必 403 → 用新身分原樣重建
      }
      if (/^09\d{8}$/.test(phone)) setContactPhone(phone); // 結帳聯絡電話優先帶本次設定用的電話
      setNeedIdentityCheck(false);
      setPendingReview(false); // 已完成加入設定 → 清「待核對」旗標，否則 kiosk 購物車會再導回 /kiosk/join
      setContractSigned(true);
      onDone();
    } catch (err) {
      const msg = getRegisterApiErrorMessage(err);
      setSubmitError(msg);
      appToast.error(msg);
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  const onFinishPress = () => {
    if (!canFinish) return;
    // 鐵牌比對卡選了「是，這是我的資料」→ 送出時才執行綁定（免簽約，帶入既有資料）
    if (plateMatch && plateChoice === "bind") {
      void bindByPlate();
      return;
    }
    // 【暫時隱藏合約書／電子簽名】2026-07-20 James 指示：合約簽署（含手寫電子簽名）先從流程移除。
    // 原本新客戶要先開 ContractSheet 簽名才完成；現在一律直接完成，簽名欄位不帶（後端為選填）。
    // 還原方式：解開下面兩行註解、移除其下的 void complete()，並一併還原 steps 與 ContractSheet 渲染。
    // if (isNew) setContractOpen(true); // 新客戶：先簽合約再完成
    // else void complete(); // 既有客戶：直接完成（仍會呼叫 walk-in 綁定既有 CIS）
    void complete();
  };

  /**
   * 鐵牌拍照讀到 barcode → 與「掃鋼瓶」相同比對邏輯（不受 cylinder_scan 開關限制）：
   * POST /register/cylinder-by-barcode 比對持有客戶；matched → 顯示確認卡問是否本人。
   * 多張鐵牌解到「不同客戶」→ 歸屬不明，放棄自動綁定（收卡 + 提示，之後照新客戶走、交 AW 核對）。
   */
  /** 把「這張照片解到的條碼＋命中客戶」寫回該照片。
   *  以 sourceFile 對應精準的那張（拍照解碼會帶 file）；live-scan 無 file → 退回最後一張還沒記過碼的照片。 */
  const attachPlateResult = (code: string, cisId: string | undefined, phone: string | undefined, addr: string | undefined, sourceFile?: File) => {
    setPlates((prev) => {
      let idx = sourceFile ? prev.findIndex((p) => p.file === sourceFile) : -1;
      if (idx < 0) {
        for (let k = prev.length - 1; k >= 0; k--) {
          if (!prev[k].barcode) { idx = k; break; }
        }
      }
      if (idx < 0) return prev; // 找不到對應照片（極少數）→ 不硬塞
      const next = [...prev];
      next[idx] = { ...next[idx], barcode: code, cisId, phone, addr };
      return next;
    });
  };

  const onPlateBarcode = async (code: string, sourceFile?: File) => {
    // 同一支條碼已記在某張照片上 → 重複讀，略過（去重改看照片，不再用累加 ref）
    if (plates.some((p) => p.barcode === code)) return;
    try {
      const res = await fetchCylinderByBarcode(code, lineAccessToken || "");
      const data = res?.data?.data;
      if (data?.state !== "matched" || !data.customer?.cis_id) {
        // eslint-disable-next-line no-console
        console.log(`[註冊] 鐵牌條碼比對 ${code} → ${data?.state ?? "查詢失敗"}（無持有客戶，純拍照）`);
        // 仍把「已解過碼、無命中」記到照片上，避免同張反覆重打 API
        attachPlateResult(code, undefined, undefined, undefined, sourceFile);
        return;
      }
      // 命中既有客戶一律出「是我/不是我」選項（不再比對電話——barcode 帶的不見得是手機）。
      // 是否衝突/顯示哪張，交由上方從 plates 衍生的 plateConflict/plateMatch 判定。
      // eslint-disable-next-line no-console
      console.log(`[註冊] 鐵牌條碼比對 ${code} → 命中既有客戶 cis_id=${data.customer.cis_id}`);
      attachPlateResult(code, data.customer.cis_id, data.customer.phone_masked || "", data.customer.address_masked || "", sourceFile);
    } catch {
      // 讀碼比對失敗不影響拍照流程，靜默略過
    }
  };

  /** 確認「是本人」→ walk-in 綁定既有 CIS（帶 barcode + matchedCisId，後端會重驗）。 */
  const bindByPlate = async () => {
    if (!plateMatch || plateBinding) return;
    setPlateBinding(true);
    setSubmitError("");
    try {
      const res = await registerWalkIn({
        accessToken: lineAccessToken || "",
        phoneVerifiedTicket: phoneVerifiedTicket || undefined,
        barcode: plateMatch.barcode,
        supplierId: effectiveSupplierId,
        shopCode: effectiveShopCode,
        matchedCisId: plateMatch.cisId,
        entrySource: entrySource || undefined,
        // 鐵牌綁定既有客戶：不可帶 landline（驗證用，帶了沒比中的號碼會讓後端擋下綁定），
        // 但客戶填過的市話仍要記錄 → 只帶 landline_input。
        customer: {
          name: userData?.name || userData?.customer_name || "",
          phone,
          landline_input: entryLandline || undefined,
        },
      });
      const data = res.data?.data;
      // eslint-disable-next-line no-console
      console.log(`[註冊] 鐵牌綁定(bindByPlate) 成功 → cis_id=${data?.cis_id}｜customer_id=${data?.customer_id}｜review_status=${data?.review_status}（barcode=${plateMatch.barcode}）`);
      if (data?.cis_id) setMatchedCisId(data.cis_id);
      const jwt = await refreshCustomerSession(lineAccessToken || "");
      if (jwt) {
        setCusJwtToken(jwt);
        const nextCtx = await refreshIdentityContexts(jwt);
        await rebuildDraftForContext(jwt, nextCtx); // 舊 context 的草稿對新身分必 403 → 用新身分原樣重建
      }
      if (/^09\d{8}$/.test(phone)) setContactPhone(phone); // 結帳聯絡電話優先帶本次設定用的電話
      setNeedIdentityCheck(false);
      setPendingReview(false); // 已綁定既有客戶 → 清「待核對」旗標，否則 kiosk 購物車會再導回 /kiosk/join
      setContractSigned(true);
      onDone();
    } catch (err) {
      const msg = getRegisterApiErrorMessage(err);
      // eslint-disable-next-line no-console
      console.log(`[註冊] 鐵牌綁定(bindByPlate) 失敗（barcode=${plateMatch.barcode} 宣稱 cis=${plateMatch.cisId}）: ${msg}`);
      setSubmitError(msg);
      appToast.error(msg);
      setPlateChoice(null); // 驗證不過（非本人）→ 卡片保留、退回未選，讓使用者改選「不是我」續走新客戶
    } finally {
      setPlateBinding(false);
    }
  };

  // 步驟指示：身分確認 → 家中資料 → 合約（既有客戶只到身分；skipIdentity 時不顯示身分步）
  // 【暫時隱藏合約書】步驟列同步移除「合約簽署」；還原時改回下方註解那行。
  // const steps = skipIdentity ? ["家中資料", "合約簽署"] : isNew ? ["身分確認", "家中資料", "合約簽署"] : ["身分確認", "完成"];
  const steps = skipIdentity ? ["家中資料", "完成"] : isNew ? ["身分確認", "家中資料", "完成"] : ["身分確認", "完成"];
  const curStep = (!identityResolved ? 0 : identityType === "existing" ? 1 : cylinderPending ? 1 : 2) - (skipIdentity ? 1 : 0);

  return createPortal(
    <div className="fjs-root" style={{ position: "fixed", inset: 0, zIndex: 1000004, background: "#EEF3FA", display: "flex", flexDirection: "column" }}>
      {/* 開啟動畫（portal 內自帶，避免依賴 kiosk.scss 是否在該路由載入） */}
      <style>{`
        @keyframes fjsIn { from { opacity: 0; transform: translateY(28px) scale(0.985); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .fjs-root { animation: fjsIn 0.34s cubic-bezier(0.2, 0.9, 0.3, 1); transform-origin: 50% 100%; }
        @media (prefers-reduced-motion: reduce) { .fjs-root { animation: none; } }
      `}</style>
      {/* 漸層頂區：關閉列 + hero + 步驟（同一個漸層容器，避免交界色差接縫） */}
      <YStack flexShrink={0} style={{ background: kiosk.gradPrimary }} pb={22}>
        <XStack ai="center" px={16} height={50}>
          <Text fontSize={13} color="rgba(255,255,255,0.9)" cursor="pointer" onPress={onClose}>取消</Text>
        </XStack>

        <XStack ai="center" gap={13} px={22}>
          <YStack ai="center" jc="center" width={46} height={46} borderRadius={14} backgroundColor="rgba(255,255,255,0.2)">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="5" y="4" width="14" height="17" rx="2.5" stroke="#fff" strokeWidth="1.7" />
              <path d="M9 4.5V3.6a1 1 0 011-1h4a1 1 0 011 1v.9" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" />
              <path d="M8.6 12.2l2.1 2.1 4.7-4.8" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </YStack>
          <YStack flex={1}>
            <Text fontSize={21} fontWeight="900" color="#fff" letterSpacing={-0.4}>完成加入設定</Text>
            <Text fontSize={12.5} color="rgba(255,255,255,0.9)" mt={2}>只要設定一次，之後叫瓦斯免再填</Text>
          </YStack>
        </XStack>

        {/* 步驟指示：連接線 flex，最後一步 flexShrink 收右緣，讓整列滿寬邊到邊 */}
        <XStack ai="center" gap={0} mt={18} px={16}>
          {steps.map((s, i) => {
            const done = i < curStep;
            const active = i === curStep;
            const last = i === steps.length - 1;
            return (
              <XStack key={s} ai="center" flex={last ? 0 : 1} flexShrink={0}>
                <YStack ai="center" gap={5} flexShrink={0}>
                  <YStack ai="center" jc="center" width={26} height={26} borderRadius={13} backgroundColor={done || active ? "#fff" : "rgba(255,255,255,0.28)"}>
                    <Text fontSize={12.5} fontWeight="900" color={done || active ? kiosk.blueD : "#fff"}>{done ? "✓" : i + 1}</Text>
                  </YStack>
                  <Text fontSize={10.5} fontWeight={active ? "800" : "600"} color={active ? "#fff" : "rgba(255,255,255,0.75)"}>{s}</Text>
                </YStack>
                {!last && <YStack flex={1} height={2} mb={16} mx={4} borderRadius={1} backgroundColor={i < curStep ? "#fff" : "rgba(255,255,255,0.28)"} />}
              </XStack>
            );
          })}
        </XStack>
      </YStack>

      {/* 白色圓角內容區 */}
      <YStack flex={1} style={{ overflow: "hidden" }} backgroundColor="#F6F8FC" borderTopLeftRadius={22} borderTopRightRadius={22} mt={-14}>
        <ScrollView flex={1} contentContainerStyle={{ padding: 16, paddingBottom: 24 }}>
          {!identityResolved ? (
            <IdentityCheckCard
              address={address}
              phone={phone}
              supplierId={effectiveSupplierId}
              assumeMatched={assume}
              onResolved={(r) => {
                setIdentityResolved(true);
                setIdentityType(r.type);
                if (r.type === "existing") {
                  setIdentityHouseNo(r.houseNo || undefined);
                  setPendingReview(false);
                }
              }}
            />
          ) : identityType === "existing" ? (
            <YStack ai="center" jc="center" gap={12} py={40}>
              <YStack ai="center" jc="center" width={72} height={72} borderRadius={36} backgroundColor="#10B981" className="kiosk-ringpop">
                <Text fontSize={38} fontWeight="900" color="#fff">✓</Text>
              </YStack>
              <Text fontSize={17} fontWeight="900" color={kiosk.ink}>已確認為既有客戶</Text>
              <Text fontSize={13} color={kiosk.grey} textAlign="center">資料已為您帶入，點下方即可完成並開始叫瓦斯。</Text>
            </YStack>
          ) : (
            <>
              <CylinderUsageCard value={cylUsage} onChange={setCylUsage} />
              {cylUsage.has === true && (
                // 鐵牌讀碼一律啟用（不受 cylinder_scan 限制，2026-07-15 拍板）；多客戶衝突由 onPlateBarcode 判定。
                // disablePlateMatch（/kiosk/join）：onboarding 已比對過身分 → 這裡純拍照，不再讀碼比對出「是我/不是我」卡。
                // 首頁入口不帶此旗標：身分卡雖已略過（onboarding 問過了），鐵牌讀碼仍是有效的比對手段，要保留。
                <PlateCard plates={plates} setPlates={setPlates} scanEnabled={!disablePlateMatch} onBarcode={disablePlateMatch ? undefined : (c) => void onPlateBarcode(c)} />
              )}
              {plateMatch && (
                // 鐵牌條碼比對到既有客戶 → 確認是否本人（是 → 免填直接綁定；不是 → 繼續新客戶）
                <YStack backgroundColor="#FFF7E6" borderColor="#F5C97B" borderWidth={1} borderRadius={14} p={14} mt={12} gap={10}>
                  <Text fontSize={14} fontWeight="800" color={kiosk.ink}>
                    這支鋼瓶登記於既有客戶
                  </Text>
                  {/* 隱私：不顯示比對到的既有客戶姓名（連遮罩後的也不行），改用遮罩電話＋遮罩地址
                      讓客戶自行確認，與 IdentityCheckCard/ExistingPhone 的確認方式一致。 */}
                  {(plateMatch.phone || plateMatch.addr) && (
                    <YStack gap={2}>
                      {!!plateMatch.phone && (
                        <Text fontSize={13} color={kiosk.ink} fontWeight="700">
                          {plateMatch.phone}
                        </Text>
                      )}
                      {!!plateMatch.addr && (
                        <Text fontSize={13} color={kiosk.ink} lineHeight={19}>
                          {plateMatch.addr}
                        </Text>
                      )}
                    </YStack>
                  )}
                  <Text fontSize={13} color={kiosk.grey}>
                    是您本人嗎？確認後免填資料直接綁定。
                  </Text>
                  {/* 純選項（不打 API）：選擇後由最下方按鈕送出時才執行綁定/新客戶建立 */}
                  <XStack gap={10}>
                    <YStack flex={1} ai="center" jc="center" height={42} borderRadius={12} borderWidth={plateChoice === "bind" ? 2 : 1} borderColor={plateChoice === "bind" ? kiosk.blue : kiosk.line} backgroundColor={plateChoice === "bind" ? "#EFF6FF" : "#fff"} cursor="pointer" onPress={() => setPlateChoice("bind")}>
                      <Text fontSize={13.5} fontWeight="800" color={plateChoice === "bind" ? kiosk.blueD : kiosk.ink}>{plateChoice === "bind" ? "✓ 是，這是我的資料" : "是，這是我的資料"}</Text>
                    </YStack>
                    <YStack flex={1} ai="center" jc="center" height={42} borderRadius={12} borderWidth={plateChoice === "new" ? 2 : 1} borderColor={plateChoice === "new" ? kiosk.blue : kiosk.line} backgroundColor={plateChoice === "new" ? "#EFF6FF" : "#fff"} cursor="pointer" onPress={() => setPlateChoice("new")}>
                      <Text fontSize={13.5} fontWeight="700" color={plateChoice === "new" ? kiosk.blueD : kiosk.grey}>{plateChoice === "new" ? "✓ 不是我（以新客戶繼續）" : "不是我"}</Text>
                    </YStack>
                  </XStack>
                </YStack>
              )}
              <AccessoryCard value={needAccessory} onChange={setNeedAccessory} />
            </>
          )}
        </ScrollView>

      <YStack flexShrink={0} backgroundColor="#fff" borderTopWidth={1} borderColor={kiosk.line} px={16} pt={12} pb={22} gap={8}>
        {!!submitError && (
          <Text fontSize={12.5} color={kiosk.danger} textAlign="center">{submitError}</Text>
        )}
        <YStack ai="center" jc="center" height={52} borderRadius={16} backgroundColor={canFinish ? kiosk.blue : "#CBD5E1"} cursor={canFinish ? "pointer" : "default"} onPress={onFinishPress} pressStyle={canFinish ? { opacity: 0.9 } : undefined}>
          <Text fontSize={15.5} fontWeight="800" color="#fff">
            {submitting || plateBinding
              ? "送出中…"
              : !identityResolved
                ? "請先完成上方身分確認"
                : cylinderPending
                  ? "請先填鋼瓶數量與用途"
                  : plateMatchPending
                    ? "請先確認鋼瓶客戶是否為您本人"
                    : plateMatch && plateChoice === "bind"
                      ? "綁定我的資料並完成"
                      : isNew
                        // 【暫時隱藏合約書】已不再開合約簽名頁 → 按鈕不可再說「閱讀合約」。
                        // 還原合約時改回 "閱讀合約並完成加入"。
                        ? "完成加入"
                        : "完成，前往下單"}
          </Text>
        </YStack>
      </YStack>
      </YStack>

      {/* 【暫時隱藏合約書／電子簽名】還原時解開此行註解即可（complete 仍接受 signatureDataUrl）。
      <ContractSheet open={contractOpen} onClose={() => setContractOpen(false)} onSign={(dataUrl) => { setContractOpen(false); void complete(dataUrl); }} /> */}
      {/* 等待安排客戶完成設定 → 「資料已備妥」收尾頁；回首頁時才 onDone 收掉整個設定流程 */}
      {waitingDone && <WaitingSetupDone onGoHome={onDone} />}
    </div>,
    document.body
  );
};

export default FirstJoinSetup;

